import { Course, CourseTraditional } from "@/models/courses.model";
import { Pagination, QueryParams } from "@/models/query-params.model";
import { CategoriesRest } from "@/services/rest/categories.rest";
import { CourseStudentRest } from "@/services/rest/courses-student.rest";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { QuizRest } from "@/services/rest/quiz.rest";
import { UsersRest } from "@/services/rest/users.rest";
import { GenericsUtils } from "@/utils/generics.util";
import {
  BehaviorSubject,
  Observable,
  Subject,
  last,
  lastValueFrom,
  map,
  take,
} from "rxjs";

const needsToPause$ = new BehaviorSubject<any>(true);
const needsToPause = needsToPause$.asObservable();

class CourseStudentService {
  private classroomId: string | undefined;

  constructor(
    private _user = new UsersRest(),
    private _courses = new CourseStudentRest(),
    private _materials = new MaterialsRest(),
    private _categories = new CategoriesRest(),
    private _generics = new GenericsUtils(),
    private _quiz = new QuizRest()
  ) {
    this.classroomId = localStorage.getItem("classId") ?? undefined;
  }

  private user$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  user: Observable<any> = this.user$.asObservable();

  private course$: Subject<any> = new Subject<any>();
  course: Observable<any> = this.course$.asObservable();

  private courses$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  courses: Observable<any> = this.courses$.asObservable();

  private trails$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  trails: Observable<any> = this.trails$.asObservable();

  private percentage$ = new Subject<number>();
  percentage = this.percentage$.asObservable();

  private categories$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  categories: Observable<any> = this.categories$.asObservable();

  private material$: Subject<any> = new Subject<any>();
  material: Observable<any> = this.material$.asObservable();

  private materials$: Subject<any> = new Subject<any>();
  materials: Observable<any> = this.materials$.asObservable();

  private myPerformance$: Subject<any> = new Subject<any>();
  myPerformance: Observable<any> = this.myPerformance$.asObservable();

  private certificate$: Subject<any> = new Subject<any>();
  certificate: Observable<any> = this.certificate$.asObservable();

  private quiz$: Subject<any> = new Subject<any>();
  quiz: Observable<any> = this.quiz$.asObservable();

  role: string = "";

  getMaterialsByLearningObjective(
    learningObjectivesId: string,
    params: Partial<QueryParams>
  ): void {
    this._courses
      .getMaterialsByLearningObjective(learningObjectivesId, params)
      .pipe(
        take(1),
        map((e) => {
          let materials = e.content;
          materials.forEach((material) => {
            material.file.url = this._generics.getSecureUrl(
              material?.file?.url
            );
            material.file.mimetype = this._generics.getMimeType(
              material?.file?.mimetype
            );
          });
          return materials;
        })
      )
      .subscribe({
        next: (response) => {
          this.materials$.next(response);
        },
      });
  }

  getMaterialsByTrail(trailId: string): void {
    this._courses
      .getMaterialsByTrail(trailId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.materials$.next(response);
        },
      });
  }

  async getCoursesByUser(
    userId: string,
    params: QueryParams
  ): Promise<Pagination<Course>> {
    const courses$ = this._courses.getCoursesByUserId(userId, params);
    return (this.courses = await lastValueFrom(courses$));
  }

  async getCourseByIdGraphs(userId: string, courseId: string): Promise<Course> {
    const course$ = this._courses.getCourseByIdGraphs(userId, courseId, {
      classroomId: this.classroomId,
    });
    return (this.course = await lastValueFrom(course$));
  }

  getCourseById(userId: string, courseId: string): void {
    this._courses
      .getCourseById(userId, courseId, { classroomId: this.classroomId })
      .pipe(take(1))
      .subscribe((response) => {
        this.course$.next(response);
      });
  }

  getCategories(params: QueryParams): void {
    this._categories
      .getCategories(params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.categories$.next(response.content);
        },
      });
  }

  getUserStudent(userId: string): void {
    this._user
      .getUserStudentById(userId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.role = response.role;
          this.user$.next(response);
        },
      });
  }

  getUserById(userId: string): void {
    this._user
      .getUserById(userId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.user$.next(response);
        },
      });
  }

  getChaptersByCourse(userId: string, courseId: string): void {
    this._courses
      .getChaptersByCourse(courseId)
      .pipe(take(1))
      .subscribe((response) => {
        this.trails$.next(response);
      });
  }

  getMaterialsById(trailId: string, materialId: string): void {
    this._courses
      .getMaterialById(trailId, materialId)
      .pipe(
        take(2),
        last(),
        map((e) => {
          e.file.url = this._generics.getSecureUrl(e?.file?.url);
          e.file.mimetype = this._generics.getMimeType(e?.file?.mimetype);
          return e;
        })
      )
      .subscribe({
        next: (response) => {
          this.material$.next(response);
        },
      });
  }

  getLearningObjectiveByChapter(chapterId: string): void {
    this._materials.getLearningObjectiveByChapter(chapterId).subscribe({
      next: (response) => {
        this.materials$.next(response);
      },
    });
  }
  getMaterials(
    courseId: string,
    userId: string,
    routeName: string,
    learningObjectiveId?: string
  ): void {
    const classroomId = localStorage.getItem("classId") ?? undefined;
    this._materials
      .getMaterials(courseId, userId, routeName, learningObjectiveId, {
        classroomId: this.classroomId,
      })
      .pipe(
        take(2),
        last(),
        map((e) => {
          if (!e.done) {
            e.learningObjective.chapter = e.chapter.name;
            e.learningObjective.chapterDescription = e.chapter.description;
            e.materials?.forEach((material) => {
              material.file.url = this._generics.getSecureUrl(
                material?.file?.url
              );
              material.file.mimetype = this._generics.getMimeType(
                material?.file?.mimetype
              );
            });
          }
          return e;
        })
      )
      .subscribe({
        next: (response) => {
          this.materials$.next(response);
        },
      });
  }
  getUserResultsHistory(userId: string, courseId: string): void {
    this._user
      .getUserResultsHistory(userId, courseId, {
        classroomId: this.classroomId,
      })
      .pipe(take(1))
      .subscribe((response) => {
        this.myPerformance$.next(response);
      });
  }
  getCourseCertificate(
    courseId: string,
    userId: string,
    classroomId?: string
  ): void {
    this._courses
      .getCertificate(courseId, userId, {
        classroomId,
      })
      .subscribe({
        next: (response) => {
          this.certificate$.next(response);
        },
      });
  }

  async getTraditionalCourseById(courseId: string): Promise<CourseTraditional> {
    const course$ = this._courses.getTraditionalCourseById(courseId, {
      classroomId: this.classroomId,
    });
    return (this.course = await lastValueFrom(course$));
  }

  getTraditionalCoursePercentage(courseId: string): void {
    this._courses
      .getTraditionalCourseById(courseId, { classroomId: this.classroomId })
      .pipe(take(1))
      .subscribe((response) => {
        this.percentage$.next(response?.percentageDone);
      });
  }
}

export { CourseStudentService, needsToPause, needsToPause$ };
